import { defineSchema } from '../../schema-meta.ts';
import { registerCategorySchema } from '../../registry.ts';
import { registerSchema } from '../../generic-codec.ts';
import type { AllRawData } from '../../raw-types.ts';

const CharacterSkinSchema = defineSchema({
  name: 'characterSkin',
  fields: [
    ['id', 'number'],
    ['cost', { kind: 'dict', dict: 'delivery' }],
    /** HP bonus from the skin's `stats.HP`. 0 when the skin sheet has no HP row. */
    ['hp', 'number?'],
    /**
     * 컬렉션 v9 등급(수동 지정값, DB: skin.grade). 미지정 = undefined(키 생략) —
     * 소비 측은 grade 0으로 소비해 0점 처리한다(fallback 아님, design §B-1 "시트에
     * 없는 grade=0점"과 동일 규칙 — SkinData.grade 주석 참조).
     */
    ['grade', 'number?'],
    /**
     * 조건 언락(skin_unlock, brief G1·G3·G4). 신규 필드는 반드시 append-only trailing
     * 으로 유지 — grade 뒤에 붙인다. 배열 원소를 named positional sub-schema 로 표현하면
     * gap-null 함정(pet.ts breakthroughs 사례)이 재현되므로, collectionConfig 슬롯과
     * 동일하게 opaque `{kind:'json'}` 으로 통째 저장한다(원소 개별 디코드 불필요 —
     * 서버가 배열 전체를 그대로 소비). 미지정 = 조건 없음(키 생략, trailing null 압축).
     */
    ['unlockConditions', { kind: 'json' }],
    /**
     * @deprecated v3 정률 공식 모델(DB: skin.enhance_stats)이 쓰던 슬롯. 모든
     * 스킨이 `upgrades`(아래) 레벨 테이블로 마이그레이션 완료(2026-09)돼 더 이상
     * 채우지 않는다 — `characterFromRaw` 가 항상 키를 생략한다. 코덱 인코딩이
     * **위치 기반 튜플**(generic-codec.ts `encode`)이라 슬롯을 삭제/재배치하면
     * 뒤 필드(`upgrades`) 인덱스가 밀려 이미 시딩된 데이터의 디코드가 깨진다 —
     * 자리만 예약해 두고 값은 절대 넣지 않는다(재사용·삭제 금지).
     */
    ['enhanceStats', { kind: 'json' }],
    /**
     * per-skin 레벨 테이블(DB: skin_upgrade)의 HP 축약 행 `[{level, hp}]`.
     * 레벨별 **절대값**이며 공식을 타지 않는다 — 모든 스킨이 이 표를 가지므로
     * 서버 권위 HP(`resolveSkinHp`)는 이 표만 본다. 배열 원소를 named positional
     * sub-schema 로 쪼개면 gap-null 함정(pet.ts breakthroughs 사례)이 재현되므로
     * `unlockConditions` 와 동일하게 opaque `{kind:'json'}` 로 통째 저장한다.
     * append-only trailing — enhanceStats 뒤.
     */
    ['upgrades', { kind: 'json' }],
  ],
});
registerSchema(CharacterSkinSchema);

const CharacterUpgradeSchema = defineSchema({
  name: 'characterUpgrade',
  fields: [
    ['id', 'number'],
    ['maxLevel', 'number'],
    ['costs', { kind: 'array', of: { kind: 'dict', dict: 'delivery' } }],
    /** Per-level HP increment from `upgrade_config.stat_increments[].HP`.
     *  Server reads this to compute `maxHp` server-side without trusting the client. */
    ['hpIncrements', { kind: 'array', of: 'number' }],
  ],
});
registerSchema(CharacterUpgradeSchema);

export const CharacterSchema = defineSchema({
  name: 'serverCharacters',
  fields: [
    ['id', 'number'],
    /** Character's base HP — `character.stats.HP`. */
    ['baseHp', 'number?'],
    ['skins', { kind: 'array', of: 'characterSkin' }],
    ['upgrades', { kind: 'array', of: 'characterUpgrade' }],
  ],
  lookupKeys: ['id'],
});

export interface RawCharacterRow {
  id: number;
  stats?: Record<string, number> | null;
}

function numericHp(value: unknown): number {
  if (value == null) return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

interface SkinUnlockConditionOut {
  type: string;
  param1: number;
  param2: number;
}
/** skin_unlock rows → skinId 별 조건 배열(condition_index 오름차순). AND 최대 2. */
function groupSkinUnlockConditions(
  rows: AllRawData['skin_unlock']
): Map<number, SkinUnlockConditionOut[]> {
  const map = new Map<number, SkinUnlockConditionOut[]>();
  const sorted = [...rows].sort(
    (a, b) => Number(a.condition_index ?? 0) - Number(b.condition_index ?? 0)
  );
  for (const r of sorted) {
    const skinId = Number(r.skin_id);
    const list = map.get(skinId) ?? [];
    list.push({
      type: String(r.condition_type),
      param1: Number(r.param_1 ?? 0),
      param2: Number(r.param_2 ?? 0),
    });
    map.set(skinId, list);
  }
  return map;
}

interface SkinUpgradeHpOut {
  level: number;
  hp: number;
}

/**
 * skin_upgrade 행 → skinId 별 `[{level, hp}]`(레벨 오름차순). 서버 권위 HP 만
 * 필요하므로 stats 에서 HP 만 뽑아 싣는다 — 나머지 스탯·스킬은 클라 전용
 * (clientCharacters 의 skins[].upgrades) 이고 서버는 보지 않는다.
 */
function groupSkinUpgradeHp(rows: AllRawData['skin_upgrade']): Map<number, SkinUpgradeHpOut[]> {
  const map = new Map<number, SkinUpgradeHpOut[]>();
  for (const r of rows ?? []) {
    const skinId = Number((r as Record<string, unknown>).skin_id);
    const level = Number((r as Record<string, unknown>).level);
    if (!Number.isFinite(skinId) || !Number.isFinite(level)) continue;
    const stats = (r as Record<string, unknown>).stats as
      | Record<string, unknown>
      | null
      | undefined;
    const list = map.get(skinId) ?? [];
    list.push({ level, hp: numericHp(stats?.HP) });
    map.set(skinId, list);
  }
  for (const list of map.values()) list.sort((a, b) => a.level - b.level);
  return map;
}

export function characterFromRaw(
  row: RawCharacterRow,
  // `skin_upgrade` 는 optional — sync_schema 전파 전 환경(테이블 미존재)에서 EF 가
  // 이 키 없이 호출할 수 있고, 그때는 레벨 테이블 없는 스킨(신규 authoring 중)으로
  // 그대로 동작한다(flat stats/skills, 증분 없음 — v3 공식 폴백은 제거됨).
  raw: Pick<AllRawData, 'skin' | 'upgrade_config' | 'skin_unlock'> &
    Partial<Pick<AllRawData, 'skin_upgrade'>>
) {
  const baseHp = numericHp((row.stats as Record<string, unknown> | null | undefined)?.HP);

  const unlockBySkin = groupSkinUnlockConditions(raw.skin_unlock ?? []);
  const levelTableBySkin = groupSkinUpgradeHp(raw.skin_upgrade ?? []);

  const skins = (raw.skin ?? [])
    .filter((s) => s.character_id === row.id)
    .map((s) => {
      const skinStats = s.stats as Record<string, unknown> | null | undefined;
      const out: {
        id: unknown;
        cost: unknown;
        hp: number;
        grade?: number;
        unlockConditions?: SkinUnlockConditionOut[];
        upgrades?: SkinUpgradeHpOut[];
      } = {
        id: s.id,
        cost: s.cost ?? {},
        hp: numericHp(skinStats?.HP),
      };
      // 키 생략 규칙 — grade 미지정(null/undefined)이면 키 자체를 만들지 않는다
      // (SkinData.grade?: number 계약 그대로, 폴백 상수 금지).
      if (s.grade != null) out.grade = Number(s.grade);
      // 조건도 동일 규칙 — 행 없으면 키 생략(조건 없는 스킨 = 즉시 구매 가능).
      const conds = unlockBySkin.get(Number(s.id));
      if (conds && conds.length > 0) out.unlockConditions = conds;
      // 레벨 테이블(skin_upgrade). 행 없는 스킨(신규 authoring 중) = 키 생략
      // — grade/unlockConditions 와 동일한 키 생략 규칙. `enhanceStats` 슬롯은
      // 더 이상 채우지 않는다(위 CharacterSkinSchema 주석 참조).
      const levels = levelTableBySkin.get(Number(s.id));
      if (levels && levels.length > 0) out.upgrades = levels;
      return out;
    });

  const upgrades = (raw.upgrade_config ?? []).map((u) => {
    const increments = Array.isArray(u.stat_increments) ? u.stat_increments : [];
    const hpIncrements = increments.map((inc: Record<string, unknown> | null | undefined) =>
      numericHp(inc?.HP)
    );
    return {
      id: u.id,
      maxLevel: u.max_level,
      costs: u.costs ?? [],
      hpIncrements,
    };
  });

  return {
    id: row.id,
    baseHp,
    skins,
    upgrades,
  };
}

registerCategorySchema('serverCharacters', CharacterSchema);
